import api, { apiUrl, assetsUrl, methods } from "@api";
import { useAuth } from "@provider/context-auth";
import { queryClient } from "@provider/query-client";
import { getAuthToken } from "@provider/token";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Platform } from "react-native";

import type { PickedImage } from "./pick-image";

// Fotobeheer: alleen voor accounts op de allowlist van de server
// (DGV_BEHEER_EMAILS). De server controleert dat bij elk verzoek; de app
// gebruikt /beheer/me alleen om het menu-item te tonen.

export type BeheerKind = "product" | "recipe" | "category";

export const kindLabels: Record<BeheerKind, string> = {
  product: "Producten",
  recipe: "Recepten",
  category: "Categorieën",
};

export type BeheerItem = {
  kind: BeheerKind;
  key: string;
  name: string;
  subtitle: string;
  image_path: string;
  image_version: string;
  image_url: string;
  has_image: boolean;
  problem?: string;
};

export type BeheerUploadResult = BeheerItem & {
  width?: number;
  height?: number;
  bytes: number;
  sha256: string;
  history?: string;
};

export type BeheerHistoryItem = {
  id: string;
  time: string;
  bytes: number;
  uploaded_by: string;
  archived_by: string;
  sha256: string;
};

export type BeheerAuditEntry = {
  time: string;
  user_id: number;
  email: string;
  action: "upload" | "restore";
  kind: BeheerKind;
  key: string;
  name?: string;
  object: string;
  old_sha256: string;
  new_sha256: string;
  version: string;
  history?: string;
  from?: string;
  ip: string;
};

type Me = { email: string; storage: boolean; max_bytes: number; max_dimension: number };

// useIsBeheerder: één goedkope /beheer/me per ingelogde sessie (gecachet, geen
// retry: een 403 is het normale antwoord voor klanten). Niet ingelogd = geen
// verzoek.
export function useIsBeheerder() {
  const { token } = useAuth();
  const query = useQuery({
    queryKey: ["beheer-me", token],
    queryFn: () => api<Me>({ path: "/beheer/me" }),
    enabled: !!token,
    retry: false,
    throwOnError: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return {
    isBeheerder: !!token && query.isSuccess && !!query.data?.email,
    me: query.data,
    isLoading: !!token && query.isLoading,
  };
}

// Afbeelding van een item via de eigen assets-host (in development de proxy),
// met de versie als cache-buster. Zonder versie een tijdstempel, zodat het
// detailscherm nooit een oude cache toont.
export function beheerImageUri(item: Pick<BeheerItem, "image_path" | "image_version">, fallbackBust?: number) {
  if (!item.image_path) {
    return "";
  }
  const bust = item.image_version ? `v=${encodeURIComponent(item.image_version)}` : `t=${fallbackBust ?? Date.now()}`;
  return `${assetsUrl}${item.image_path}?${bust}`;
}

export function useBeheerSearch(kind: BeheerKind, q: string, enabled: boolean) {
  return useQuery({
    queryKey: ["beheer-search", kind, q],
    queryFn: () => api<BeheerItem[]>({ path: "/beheer/search", params: { kind, q } }),
    enabled: enabled && (kind === "category" || q.trim().length > 0),
    throwOnError: false,
    retry: false,
  });
}

export function useBeheerItem(kind: string, key: string) {
  return useQuery({
    queryKey: ["beheer-item", kind, key],
    queryFn: () => api<BeheerItem>({ path: "/beheer/item", params: { kind, key } }),
    throwOnError: false,
    retry: false,
  });
}

export function useBeheerHistory(kind: string, key: string, enabled: boolean) {
  return useQuery({
    queryKey: ["beheer-history", kind, key],
    queryFn: () => api<BeheerHistoryItem[]>({ path: "/beheer/history", params: { kind, key } }),
    enabled,
    throwOnError: false,
    retry: false,
  });
}

export function useBeheerPreview(kind: string, key: string, id: string) {
  return useQuery({
    queryKey: ["beheer-preview", kind, key, id],
    queryFn: () => api<{ data: string }>({ path: "/beheer/history/preview", params: { kind, key, id } }),
    staleTime: Infinity,
    throwOnError: false,
    retry: false,
  });
}

export function useBeheerAudit() {
  return useQuery({
    queryKey: ["beheer-audit"],
    queryFn: () => api<BeheerAuditEntry[]>({ path: "/beheer/audit" }),
    throwOnError: false,
    retry: false,
  });
}

// Na een upload of terugzetten: beheerdata én de catalogus opnieuw ophalen,
// zodat ook deze app de nieuwe foto meteen toont.
function refreshAfterChange(kind: string, key: string) {
  queryClient.invalidateQueries({ queryKey: ["beheer-item", kind, key] });
  queryClient.invalidateQueries({ queryKey: ["beheer-history", kind, key] });
  queryClient.invalidateQueries({ queryKey: ["beheer-search"] });
  queryClient.invalidateQueries({ queryKey: ["beheer-audit"] });
  queryClient.invalidateQueries({ queryKey: ["products"] });
  queryClient.invalidateQueries({ queryKey: ["categories"] });
  queryClient.invalidateQueries({ queryKey: ["recipes"] });
}

export function useBeheerRestore(kind: string, key: string) {
  return useMutation({
    mutationFn: (id: string) =>
      api<BeheerUploadResult>({ path: "/beheer/restore", method: methods.POST, body: { kind, key, id } }),
    onSuccess: () => refreshAfterChange(kind, key),
  });
}

// uploadImage: multipart via XMLHttpRequest, want fetch kent geen
// upload-voortgang. De server accepteert ook een kale body met ?kind=&key=,
// mocht een tussenstation multipart niet doorgeven.
function uploadImage({
  kind,
  key,
  image,
  onProgress,
}: {
  kind: string;
  key: string;
  image: PickedImage;
  onProgress?: (fraction: number) => void;
}): Promise<BeheerUploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(methods.POST, `${apiUrl}/beheer/images`);
    xhr.timeout = 3 * 60 * 1000;
    xhr.setRequestHeader("Accept", "application/json");
    const token = getAuthToken();
    if (token) {
      xhr.setRequestHeader("Authorization", "Bearer " + token);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress?.(e.loaded / e.total);
      }
    };
    xhr.onload = () => {
      let json: any;
      try {
        json = JSON.parse(xhr.responseText);
      } catch {}
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(json as BeheerUploadResult);
        return;
      }
      if (xhr.status === 413) {
        reject(new Error("Het bestand is te groot (maximaal 15 MB)."));
        return;
      }
      reject(new Error(json?.message || `Uploaden is mislukt (${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error("Geen verbinding met de server. Probeer het opnieuw."));
    xhr.ontimeout = () => reject(new Error("Het uploaden duurde te lang. Probeer het opnieuw."));

    const form = new FormData();
    form.append("kind", kind);
    form.append("key", key);
    if (Platform.OS === "web") {
      form.append("file", image.file as Blob, image.name);
    } else {
      form.append("file", image.file as any);
    }
    xhr.send(form);
  });
}

export function useBeheerUpload(kind: string, key: string, onProgress: (fraction: number) => void) {
  return useMutation({
    mutationFn: (image: PickedImage) => uploadImage({ kind, key, image, onProgress }),
    onSuccess: () => refreshAfterChange(kind, key),
  });
}

export function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatBytes(n: number) {
  if (!n) {
    return "";
  }
  if (n < 1024 * 1024) {
    return `${Math.round(n / 1024)} kB`;
  }
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
